import React, { useState, useEffect, useRef } from 'react';
import { Form, Button, Container, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { useNavigate, useParams } from "react-router-dom";
import { checkIfUserHasAPwdRequest, updatePassword } from '../services/Login';
import './Login.css';

function ResetPassword() {
  const { id: token } = useParams();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showAlert, setShowAlert] = useState(false); 
  const [alertText, setAlertText] = useState(''); 
  const [alertVariant, setAlertVariant] = useState('warning');
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isValidRequest, setIsValidRequest] = useState(false);
  const [passwordUpdated, setPasswordUpdated] = useState(false);
  const [userInfo, setUserInfo] = useState({ userName: '', email: '' });
  const [updateToken, setUpdateToken] = useState('');
  const hasCheckedRequest = useRef(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (hasCheckedRequest.current) {
      return;
    }

    if (!token) {
      setIsLoading(false);
      setIsValidRequest(false);
      setShowAlert(true);
      setAlertText('Solicitud inválida. No se proporcionó un token de usuario.');
      setAlertVariant('danger');
      hasCheckedRequest.current = true;
      return;
    }

    hasCheckedRequest.current = true;

    checkIfUserHasAPwdRequest(token).then((checkResult) => {
      if (checkResult.result) {
        setIsValidRequest(true);
        setUserInfo({
          userName: checkResult.userName || '',
          email: checkResult.email || ''
        });
        setUpdateToken(checkResult.updateToken || '');
      } else {
        setIsValidRequest(false);
        setShowAlert(true);
        setAlertText('Esta solicitud de restablecimiento de contraseña no es válida o ya expiró. Por favor, solicita un nuevo enlace de recuperación.');
        setAlertVariant('danger');
      }
    }).catch(() => {
      setIsValidRequest(false);
      setShowAlert(true);
      setAlertText('Ocurrió un error al verificar tu solicitud. Por favor, intenta nuevamente más tarde.');
      setAlertVariant('danger');
    }).finally(() => {
      setIsLoading(false);
    });
  }, [token]);

  const handleBackToLogin = () => {
    navigate('/login');
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setIsButtonDisabled(true);
    setShowAlert(false);

    let pendingInputs = [];

    if (!password) {
      pendingInputs.push("Contraseña");
    }
    if (!confirmPassword) {
      pendingInputs.push("Confirmar contraseña");
    }

    if (pendingInputs.length > 0) {
      setShowAlert(true);
      setAlertText(`Los siguientes campos son requeridos: ${pendingInputs.join(', ')}`);
      setAlertVariant('warning');
      setIsButtonDisabled(false);
      return;
    }

    if (password.length < 6) {
      setShowAlert(true);
      setAlertText('La contraseña debe tener al menos 6 caracteres.');
      setAlertVariant('warning');
      setIsButtonDisabled(false);
      return;
    }

    if (password !== confirmPassword) {
      setShowAlert(true);
      setAlertText('Las contraseñas no coinciden. Por favor, verifica que ambos campos sean iguales.');
      setAlertVariant('warning');
      setIsButtonDisabled(false);
      return;
    }

    const requestBody = {
      token: updateToken,
      pwd: password
    };    
    
    updatePassword(requestBody).then((updateResult) => {
      if (updateResult.result) {
        setPasswordUpdated(true);
        setShowAlert(true);
        setAlertText('¡Tu contraseña ha sido actualizada exitosamente! Ahora puedes iniciar sesión con tu nueva contraseña.');
        setAlertVariant('success');
        setPassword('');
        setConfirmPassword('');
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        setShowAlert(true);
        setAlertText('Ocurrió un error al actualizar tu contraseña. Por favor, intenta nuevamente o solicita un nuevo enlace de recuperación.');
        setAlertVariant('danger');
        setIsButtonDisabled(false);
      }
    }).catch(() => {
      setShowAlert(true);
      setAlertText('Ocurrió un error al procesar tu solicitud. Por favor, intenta nuevamente más tarde.');
      setAlertVariant('danger');
      setIsButtonDisabled(false);
    });
  };

  if (isLoading) {
    return (
      <div className="bg-dark d-flex align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
        <Container>
          <Row className="justify-content-center">
            <Col xl={4} className='text-center'>
              <Spinner animation="border" variant="light" />
              <p className="text-white mt-3">Verificando solicitud...</p>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  if (!isValidRequest) {
    return (
      <div className="bg-dark d-flex align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
        <Container>
          <Row className="justify-content-center">
            <Col xl={4} className='text-center'>
              <div className="login-logo">
                <i className="bi bi-boxes"></i>
              </div>
              <h2 className="text-center text-white mb-4">Solicitud inválida</h2>
            </Col>
          </Row>
          <Row className="justify-content-center mt-4">
            <Col xl={4} className='text-center'>
              <Alert variant={alertVariant} show={showAlert}>
                {alertText}
              </Alert>
            </Col>
          </Row>
          <Row className="justify-content-center">
            <Col xl={4} className='text-center'>
              <Button variant="outline-light" onClick={handleBackToLogin}>
                Volver al inicio de sesión
              </Button>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  return (
    <div className="bg-dark d-flex align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
      <Container>
        <Row className="justify-content-center">
          <Col xl={4} className='text-center'>
            <div className="login-logo">
              <i className="bi bi-boxes"></i>
            </div>
            <h2 className="text-center text-white mb-2">Restablecer contraseña</h2>
            {userInfo.userName && (
              <p className="text-white-50 mb-1">
                <strong>{userInfo.userName}</strong>
              </p>
            )}
            {userInfo.email && (
              <p className="text-white-50 mb-4">
                {userInfo.email}
              </p>
            )}
            {!userInfo.userName && !userInfo.email && (
              <p className="text-white-50 mb-4">Ingresa tu nueva contraseña</p>
            )}
          </Col>
        </Row>
        <Row className="justify-content-center">
          <Col xl={4} className='text-center'>
            {!passwordUpdated && (
              <Form onSubmit={handleSubmit}>
                <Form.Group controlId="formPassword" className="mb-3">
                  <Form.Label className="text-white">Nueva contraseña</Form.Label>
                  <Form.Control
                    type="password"
                    placeholder="Ingrese su nueva contraseña"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}                   
                  />
                  <Form.Text className="text-white-50">
                    Mínimo 6 caracteres
                  </Form.Text>
                </Form.Group>
                
                <Form.Group controlId="formConfirmPassword" className="mb-3">
                  <Form.Label className="text-white">Confirmar contraseña</Form.Label>
                  <Form.Control
                    type="password"
                    placeholder="Confirme su nueva contraseña"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}                   
                  />               
                </Form.Group>
                
                <Button className='mt-3 me-2' variant="primary" type="submit" disabled={isButtonDisabled}>
                  Actualizar contraseña
                </Button>
                <Button className='mt-3' variant="outline-light" type="button" onClick={handleBackToLogin}>
                  Cancelar
                </Button>
              </Form>
            )}
            
            {passwordUpdated && (
              <div className="mt-3">
                <p className="text-white mb-3">Redirigiendo al inicio de sesión...</p>
                <Button variant="outline-light" onClick={handleBackToLogin}>
                  Ir ahora al inicio de sesión
                </Button>
              </div>
            )}
          </Col>
        </Row>
        <Row className="justify-content-center mt-4">
          <Col xl={4} className='text-center'>
            <Alert variant={alertVariant} show={showAlert}>
              {alertText}
            </Alert>
          </Col>
        </Row> 
      </Container>
    </div>
  );
}

export default ResetPassword;
